/**
 * src/pages/Acceder.jsx
 * Login para usuarios finales con email + password (JWT).
 * Redirige a checkout si viene de /pricing?tier=X
 */
import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { loginUser, crearCheckout, getMe } from '../api/billing'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export default function Acceder() {
  const [params] = useSearchParams()
  const tier = params.get('tier')
  // El ciclo elegido en la página de precios debe sobrevivir al login.
  const ciclo = params.get('ciclo') || 'mensual'
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const setJwt = useAuthStore(state => state.setJwt)
  const navigate = useNavigate()

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email || !form.password) { setError('Completa todos los campos'); return }
    setLoading(true); setError('')
    try {
      const data = await loginUser({ email: form.email, password: form.password })
      setJwt(data.access_token, { email: data.email, nombre: data.nombre, rol: data.rol })
      if (tier) {
        const { url } = await crearCheckout(tier, ciclo)
        window.location.href = url
      } else if (data.rol === 'admin') {
        navigate('/', { replace: true })
      } else {
        // Cliente sin plan preseleccionado: el destino depende de en qué punto del
        // alta está. Con tenant → su dashboard; pagó pero sin tenant → onboarding
        // (no /pricing, que era el bug); sin suscripción → a elegir plan.
        let me = null
        try { me = await getMe() } catch { /* si falla, caemos al default */ }
        if (me?.cliente_slug) {
          window.location.href = `${API_BASE}/scraping/${me.cliente_slug}/dashboard`
        } else if (me?.tier) {
          navigate('/onboarding', { replace: true })
        } else {
          navigate('/pricing', { replace: true })
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Email o contraseña incorrectos')
      } else {
        setError(err.response?.data?.detail || 'Error al iniciar sesión')
      }
    } finally { setLoading(false) }
  }

  const inputStyle = {
    width: '100%', background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)', borderRadius: '8px',
    padding: '10px 14px', fontSize: '14px', fontFamily: 'var(--font-ui)',
    color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--color-text)' }}>CM Monitor</div>
          <p style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginTop: '4px' }}>
            {tier ? `PLAN ${tier.toUpperCase()} · 14 DÍAS GRATIS` : 'INICIAR SESIÓN'}
          </p>
        </div>
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '28px', boxShadow: 'var(--shadow-lg)' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>EMAIL</label>
              <input type="email" style={inputStyle} name="email" autoComplete="email" value={form.email} onChange={set('email')}
                placeholder="tu@empresa.cl" disabled={loading}
                onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>CONTRASEÑA</label>
              <input type="password" style={inputStyle} name="password" autoComplete="current-password" value={form.password} onChange={set('password')}
                placeholder="••••••••" disabled={loading}
                onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
            </div>
            {error && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>{error}</div>
            )}
            <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? 'var(--color-accent-dim)' : 'var(--color-accent)', color: loading ? 'var(--color-accent)' : '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {loading ? <><span style={{ width: '14px', height: '14px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Entrando...</> : tier ? `Entrar y pagar plan ${tier}` : 'Iniciar sesión'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--color-text-3)' }}>
            ¿No tienes cuenta? <Link to={tier ? `/register?tier=${tier}` : '/register'} style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>Crear cuenta</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px' }}>
            <Link to="/forgot-password" style={{ color: 'var(--color-text-3)', textDecoration: 'none' }}>¿Olvidaste tu contraseña?</Link>
          </p>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
